import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, Receipt, Wallet, Settings, LogOut, Menu, X, Calendar, Table, Mail, Shield, TrendingUp, Calculator 
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Biz360Logo } from "./Biz360Logo";
import { DemoConversionPopup } from "../DemoConversionPopup";

// Liste des emails admin (doit correspondre au backend)
const ADMIN_EMAILS = ["t.samblat@gmail.com"];

// Dashboard Layout (sidebar + main content)
export const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is admin - double vérification (email local + API)
  useEffect(() => {
    // Vérification immédiate par email
    if (user?.email && ADMIN_EMAILS.includes(user.email)) {
      setIsAdmin(true);
      return;
    }
    
    // Vérification API en backup
    const checkAdmin = async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/check`, {
            headers: { "Authorization": `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setIsAdmin(data.is_admin);
          }
        }
      } catch (err) {
        // Si l'API échoue, on garde la vérification par email
        if (user?.email && ADMIN_EMAILS.includes(user.email)) {
          setIsAdmin(true);
        }
      }
    };
    checkAdmin();
  }, [user?.email]);

  // Calculate if creation date is > 1 year
  const showHistorique = useMemo(() => {
    if (!user?.date_creation_entreprise) return false;
    const creation = new Date(user.date_creation_entreprise);
    const now = new Date();
    return (now - creation) / (1000 * 60 * 60 * 24 * 365) > 1;
  }, [user?.date_creation_entreprise]);

  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Mon Tableau de Bord" },
    { path: "/ventes", icon: Receipt, label: "Mes Ventes" },
    { path: "/depenses", icon: Wallet, label: "Mes Dépenses" },
    { path: "/recap-ca", icon: Table, label: "Mon CA" },
    { path: "/simulateur", icon: Calculator, label: "Mon Simulateur" },
    ...(showHistorique ? [{ path: "/historique", icon: Calendar, label: "Mon Historique" }] : []),
    { path: "/parametres", icon: Settings, label: "Mes Paramètres" },
    { path: "/contact", icon: Mail, label: "Contact" },
    ...(isAdmin ? [
      { path: "/admin", icon: Shield, label: "Administration", admin: true },
      { path: "/admin/marketing", icon: TrendingUp, label: "Marketing", admin: true },
    ] : [])
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b z-30 flex items-center justify-between px-4">
        <button onClick={() => setSidebarOpen(true)} className="p-2 hover:bg-muted rounded-lg"><Menu size={24} /></button>
        <Biz360Logo size={32} />
        <div className="w-10" />
      </div>
      <aside className={`fixed inset-y-0 left-0 w-64 bg-card border-r z-40 transform transition-transform lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-4 border-b flex items-center justify-between">
          <Biz360Logo size={36} />
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1 hover:bg-muted rounded"><X size={20} /></button>
        </div>
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <button key={item.path} onClick={() => { navigate(item.path); setSidebarOpen(false); }} data-testid={`nav-${item.path.slice(1)}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${location.pathname === item.path ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
              <item.icon size={20} /><span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium">{user?.prenom?.[0]}{user?.nom?.[0]}</div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{user?.prenom} {user?.nom}</div>
              <div className="text-sm text-muted-foreground truncate">{user?.email}</div>
            </div>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-destructive/10 text-destructive transition-colors">
            <LogOut size={20} /><span>Déconnexion</span>
          </button>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">{children}</main>
      
      {/* Popup de conversion pour le compte démo */}
      <DemoConversionPopup />
    </div>
  );
};
